import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../../lib/api.js';
import { useAuth } from '../../../context/AuthContext.jsx';

const FEEDBACK_TYPES = [
  { value: 'idea', label: 'Idea', hint: 'A suggestion to improve the dashboard or workflow.' },
  { value: 'issue', label: 'Issue', hint: 'Something broken, confusing, or wrong.' },
  { value: 'other', label: 'Other', hint: 'Anything else worth saying.' },
];

const PROBLEM_CATEGORIES = [
  { value: 'factual', label: 'Factual' },
  { value: 'voice', label: 'Voice' },
  { value: 'missing', label: 'Missing context' },
  { value: 'hallucination', label: 'Hallucination' },
  { value: 'source', label: 'Source' },
  { value: 'formatting', label: 'Formatting' },
  { value: 'other', label: 'Other' },
];

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function fmtDateTime(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function AdminFeedback() {
  const { user } = useAuth();
  const isDev = user?.role === 'dev';

  const [feedbackType, setFeedbackType] = useState('idea');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackDone, setFeedbackDone] = useState(false);

  const [isoDate, setIsoDate] = useState(todayIso());
  const [problemCategory, setProblemCategory] = useState('other');
  const [problemDescription, setProblemDescription] = useState('');
  const [problemSubmitting, setProblemSubmitting] = useState(false);
  const [problemDone, setProblemDone] = useState(false);

  const [error, setError] = useState(null);
  const [mineFeedback, setMineFeedback] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadRecent = useCallback(async () => {
    setLoading(true);
    try {
      const [fb, reports] = await Promise.all([
        api.get('/editor-feedback/mine'),
        api.get('/problem-reports/recent?limit=30'),
      ]);
      setMineFeedback(fb.feedback || []);
      setRecentReports(reports.reports || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  async function submitFeedback(e) {
    e.preventDefault();
    const trimmed = feedbackMessage.trim();
    if (!trimmed) return;
    setFeedbackSubmitting(true);
    setError(null);
    try {
      await api.post('/editor-feedback', {
        type: feedbackType,
        message: trimmed,
        view: 'admin',
      });
      setFeedbackMessage('');
      setFeedbackDone(true);
      await loadRecent();
    } catch (err) {
      setError(err.message);
    } finally {
      setFeedbackSubmitting(false);
    }
  }

  async function submitProblem(e) {
    e.preventDefault();
    const trimmed = problemDescription.trim();
    if (!trimmed) return;
    setProblemSubmitting(true);
    setError(null);
    try {
      await api.post('/problem-reports', {
        isoDate,
        targetType: 'overview',
        category: problemCategory,
        description: trimmed,
      });
      setProblemDescription('');
      setProblemDone(true);
      await loadRecent();
    } catch (err) {
      setError(err.message);
    } finally {
      setProblemSubmitting(false);
    }
  }

  return (
    <>
      <div className="mb-6">
        <h2 className="font-display text-[14px] font-bold tracking-display-md uppercase text-hxd-ink">
          Feedback &amp; Issues
        </h2>
        <p className="text-[13px] text-hxd-muted mt-1">
          Log dashboard ideas, workflow issues, or content problems.{' '}
          {isDev && (
            <Link to="/admin/dev/feedback" className="text-hxd-orange hover:underline">
              Full triage (dev)
            </Link>
          )}
        </p>
      </div>

      {error && (
        <div className="mb-5 bg-red-50 border border-red-200 rounded p-3 text-hxd-err text-[13px]">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
        <section className="hxd-card">
          <div className="hxd-card-header">Dashboard feedback</div>
          {feedbackDone ? (
            <p className="text-[13px] text-hxd-ok">
              Thanks — logged.{' '}
              <button
                type="button"
                className="underline text-hxd-muted"
                onClick={() => setFeedbackDone(false)}
              >
                Send another
              </button>
            </p>
          ) : (
            <form onSubmit={submitFeedback} className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {FEEDBACK_TYPES.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setFeedbackType(opt.value)}
                    className={
                      feedbackType === opt.value
                        ? 'px-3 py-1.5 rounded-[3px] border bg-hxd-orange text-white border-hxd-orange text-[12px] font-bold'
                        : 'px-3 py-1.5 rounded-[3px] border bg-white text-hxd-ink border-hxd-border text-[12px] font-bold hover:border-hxd-orange'
                    }
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-hxd-muted">
                {FEEDBACK_TYPES.find((t) => t.value === feedbackType)?.hint}
              </p>
              <textarea
                className="hxd-input min-h-[100px]"
                value={feedbackMessage}
                onChange={(e) => setFeedbackMessage(e.target.value)}
                maxLength={2000}
              />
              <button
                type="submit"
                disabled={feedbackSubmitting || !feedbackMessage.trim()}
                className="hxd-btn hxd-btn-primary hxd-btn-sm"
              >
                {feedbackSubmitting ? 'Sending…' : 'Send feedback'}
              </button>
            </form>
          )}
        </section>

        <section className="hxd-card">
          <div className="hxd-card-header">Problem report</div>
          {problemDone ? (
            <p className="text-[13px] text-hxd-ok">
              Report logged.{' '}
              <button
                type="button"
                className="underline text-hxd-muted"
                onClick={() => setProblemDone(false)}
              >
                Report another
              </button>
            </p>
          ) : (
            <form onSubmit={submitProblem} className="space-y-3">
              <div>
                <label className="hxd-label">Edition date</label>
                <input
                  type="date"
                  className="hxd-input"
                  value={isoDate}
                  onChange={(e) => setIsoDate(e.target.value)}
                />
              </div>
              <div>
                <label className="hxd-label">Category</label>
                <select
                  className="hxd-input"
                  value={problemCategory}
                  onChange={(e) => setProblemCategory(e.target.value)}
                >
                  {PROBLEM_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="hxd-label">Description</label>
                <textarea
                  className="hxd-input min-h-[80px]"
                  value={problemDescription}
                  onChange={(e) => setProblemDescription(e.target.value)}
                  placeholder="What's wrong?"
                />
              </div>
              <button
                type="submit"
                disabled={problemSubmitting || !problemDescription.trim()}
                className="hxd-btn hxd-btn-primary hxd-btn-sm"
              >
                {problemSubmitting ? 'Logging…' : 'Submit report'}
              </button>
            </form>
          )}
        </section>
      </div>

      <h3 className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-muted mb-3">
        Recent activity
      </h3>

      {loading ? (
        <p className="text-hxd-muted text-[13px]">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="hxd-card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-hxd-border bg-hxd-surface font-display text-[10px] font-bold uppercase tracking-display-md">
              Your dashboard notes
            </div>
            {mineFeedback.length === 0 ? (
              <p className="px-4 py-6 text-[13px] text-hxd-muted">No submissions yet.</p>
            ) : (
              <ul className="divide-y divide-hxd-border max-h-[320px] overflow-y-auto">
                {mineFeedback.map((row) => (
                  <li key={row._id} className="px-4 py-3 text-[13px]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-display text-[9px] font-bold uppercase tracking-display-sm text-hxd-orange">
                        {row.type}
                      </span>
                      <span className="text-[11px] text-hxd-muted">{fmtDateTime(row.createdAt)}</span>
                    </div>
                    <p className="text-hxd-ink m-0">{row.message}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="hxd-card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-hxd-border bg-hxd-surface font-display text-[10px] font-bold uppercase tracking-display-md">
              Recent problem reports
            </div>
            {recentReports.length === 0 ? (
              <p className="px-4 py-6 text-[13px] text-hxd-muted">No reports yet.</p>
            ) : (
              <ul className="divide-y divide-hxd-border max-h-[320px] overflow-y-auto">
                {recentReports.map((row) => (
                  <li key={row._id} className="px-4 py-3 text-[13px]">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-display text-[9px] font-bold uppercase tracking-display-sm text-hxd-warn">
                        {row.category}
                      </span>
                      <span className="text-[11px] text-hxd-muted">{row.isoDate}</span>
                      <span className="text-[11px] text-hxd-muted">{fmtDateTime(row.createdAt)}</span>
                    </div>
                    <p className="text-hxd-ink m-0">{row.description}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </>
  );
}
